import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { getStorage, saveAIPlatforms, getApiBase, resolveImageUrl } from '@/utils/storage';
import { AIPlatform, Bookmark } from '@/types';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MoreHorizontal, Edit, Trash2, X, Upload } from 'lucide-react';
import ImageWithLoader from '@/components/ImageWithLoader';

const SortableAIItem = ({ platform, onEdit, isReadOnly, variant = 'compact' }: { platform: AIPlatform, onEdit: (p: AIPlatform) => void, isReadOnly?: boolean, variant?: 'compact' | 'card' }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: platform.id, disabled: isReadOnly });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : 1,
    };

    if (variant === 'card') {
        return (
            <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="relative group shrink-0">
                <a
                    href={platform.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center gap-3 p-4 bg-white dark:bg-white/5 rounded-xl border border-slate-200 dark:border-white/10 hover:border-blue-400 dark:hover:border-blue-400 hover:shadow-lg dark:hover:shadow-blue-900/20 transition-all w-32 h-32 justify-center group-hover:scale-105"
                    onClick={(e) => {
                        if (isDragging) e.preventDefault();
                    }}
                >
                    <div className="w-12 h-12 rounded-2xl overflow-hidden shadow-sm bg-white dark:bg-slate-800 flex items-center justify-center text-2xl">
                        {platform.icon && (platform.icon.startsWith('http') || platform.icon.startsWith('data:') || platform.icon.startsWith('/')) ? (
                            <ImageWithLoader src={platform.icon} alt="" className="w-full h-full" />
                        ) : (
                            platform.icon || '🤖'
                        )}
                    </div>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200 text-center line-clamp-2 px-1">
                        {platform.name}
                    </span>
                </a>
                
                 {!isReadOnly && (
                     <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onEdit(platform);
                        }}
                        className="absolute top-2 right-2 p-1.5 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-white/10 opacity-0 group-hover:opacity-100 transition-opacity text-slate-500 hover:text-blue-500 dark:hover:text-blue-400 z-20 shadow-md"
                    >
                        <Edit size={12} />
                    </button>
                 )}
            </div>
        );
    }

    // Compact Mode (Default for Home)
    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="relative group shrink-0">
            <a
                href={platform.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 p-2 rounded-xl hover:bg-white/50 dark:hover:bg-white/10 transition-all w-24 group-hover:-translate-y-1"
                onClick={(e) => {
                    if (isDragging) e.preventDefault();
                }}
            >
                <div className="w-14 h-14 rounded-2xl overflow-hidden shadow-sm bg-white dark:bg-slate-800 flex items-center justify-center text-2xl border border-slate-200 dark:border-white/10 group-hover:shadow-md transition-all">
                    {platform.icon && (platform.icon.startsWith('http') || platform.icon.startsWith('data:') || platform.icon.startsWith('/')) ? (
                        <ImageWithLoader src={platform.icon} alt="" className="w-full h-full" />
                    ) : (
                        platform.icon || '🤖'
                    )}
                </div>
                <span className="text-xs font-medium text-slate-600 dark:text-slate-300 text-center line-clamp-1 w-full group-hover:text-slate-900 dark:group-hover:text-white">
                    {platform.name}
                </span>
            </a>
            
             {!isReadOnly && (
                 <button
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onEdit(platform);
                    }}
                    className="absolute -top-1 -right-1 p-1 bg-white dark:bg-slate-800 rounded-full border border-slate-200 dark:border-white/10 opacity-0 group-hover:opacity-100 transition-opacity text-slate-500 hover:text-blue-500 dark:hover:text-blue-400 z-20 shadow-sm"
                >
                    <Edit size={10} />
                </button>
             )}
        </div>
    );
};

interface EditModalProps {
    platform: AIPlatform | null;
    isOpen: boolean;
    onClose: () => void;
    onSave: (platform: AIPlatform) => void;
    onDelete: (id: string) => void;
}

const EditModal = ({ platform, isOpen, onClose, onSave, onDelete }: EditModalProps) => {
    const [formData, setFormData] = useState<AIPlatform>({ id: '', name: '', url: '', icon: '' });

    useEffect(() => {
        if (platform) {
            setFormData(platform);
        } else {
            setFormData({ id: Date.now().toString(), name: '', url: '', icon: '' });
        }
    }, [platform]);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) { // Limit to 2MB
                alert('图片大小不能超过 2MB');
                return;
            }
            
            const formData = new FormData();
            formData.append('file', file);

            try {
                const apiBase = getApiBase();
                // Extract base URL from apiBase (remove /api)
                const baseUrl = apiBase.replace(/\/api$/, '');
                const response = await fetch(`${baseUrl}/upload`, {
                    method: 'POST',
                    body: formData
                });
                
                if (!response.ok) throw new Error('Upload failed');
                
                const data = await response.json();
                setFormData(prev => ({ ...prev, icon: data.url }));
            } catch (error) {
                console.error('Upload error:', error);
                alert('上传失败，请确保本地服务已启动 (运行 start-server.bat)');
            }
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 p-6 rounded-2xl w-full max-w-md shadow-2xl">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-slate-900 dark:text-white">{platform ? '编辑 AI 平台' : '添加 AI 平台'}</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-900 dark:hover:text-white"><X size={20} /></button>
                </div>
                
                <div className="space-y-4">
                    <div>
                        <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">名称</label>
                        <input 
                            type="text" 
                            value={formData.name}
                            onChange={e => setFormData({...formData, name: e.target.value})}
                            className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-slate-900 dark:text-white outline-none focus:border-blue-500 dark:focus:border-blue-400"
                        />
                    </div>
                    <div>
                        <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">URL</label>
                        <input 
                            type="text" 
                            value={formData.url}
                            onChange={e => setFormData({...formData, url: e.target.value})}
                            className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-slate-900 dark:text-white outline-none focus:border-blue-500 dark:focus:border-blue-400"
                        />
                    </div>
                    <div>
                        <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">图标 (Emoji / URL / 上传图片)</label>
                        <div className="flex gap-2">
                            <input 
                                type="text" 
                                value={formData.icon}
                                onChange={e => setFormData({...formData, icon: e.target.value})}
                                className="flex-1 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-slate-900 dark:text-white outline-none focus:border-blue-500 dark:focus:border-blue-400"
                                placeholder="输入 Emoji 或粘贴图片 URL"
                            />
                            <label className="cursor-pointer flex items-center justify-center px-3 py-2 bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg hover:bg-slate-200 dark:hover:bg-white/10 transition-colors" title="上传本地图片">
                                <Upload size={16} className="text-slate-500 dark:text-slate-400" />
                                <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                            </label>
                        </div>
                        {formData.icon && (formData.icon.startsWith('data:image') || formData.icon.startsWith('http') || formData.icon.startsWith('/')) ? (
                            <div className="mt-2 w-10 h-10 rounded-full overflow-hidden border border-slate-200 dark:border-white/10">
                                <ImageWithLoader src={formData.icon} alt="Preview" className="w-full h-full" />
                            </div>
                        ) : null}
                    </div>
                </div>

                <div className="flex justify-between mt-6">
                    {platform && (
                        <button 
                            onClick={() => onDelete(platform.id)}
                            className="text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 text-sm flex items-center gap-1"
                        >
                            <Trash2 size={16} /> 删除
                        </button>
                    )}
                    <div className="flex gap-2 ml-auto">
                        <button onClick={onClose} className="px-4 py-2 text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white text-sm">取消</button>
                        <button 
                            onClick={() => onSave(formData)}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm"
                        >
                            保存
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

interface AIPlatformsProps {
    sourceTagId?: string;
    variant?: 'compact' | 'card';
}

const AIPlatforms = ({ sourceTagId, variant = 'compact' }: AIPlatformsProps) => {
  const [platforms, setPlatforms] = useState<AIPlatform[]>([]);
  const [editingPlatform, setEditingPlatform] = useState<AIPlatform | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    const loadData = async () => {
      const data = await getStorage();
      if (sourceTagId) {
          const taggedBookmarks = data.bookmarks.filter(b => b.tags.includes(sourceTagId));
          const mappedPlatforms: AIPlatform[] = taggedBookmarks.map(b => ({
              id: b.id,
              name: b.title,
              url: b.url,
              icon: b.favicon || '🔗'
          }));
          setPlatforms(mappedPlatforms);
      } else {
          setPlatforms(data.aiPlatforms);
      }
    };
    loadData();
  }, [sourceTagId]);

  const handleDragEnd = (event: DragEndEvent) => {
    if (sourceTagId) return; // Disable reorder if using tag source

    const { active, over } = event;
    if (over && active.id !== over.id) {
      setPlatforms((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        saveAIPlatforms(newItems);
        return newItems;
      });
    }
  };

  const handleEdit = (platform: AIPlatform) => {
      if (sourceTagId) return; // Disable edit if using tag source
      setEditingPlatform(platform);
      setIsModalOpen(true);
  };

  const handleSave = async (platform: AIPlatform) => {
      if (sourceTagId) return;
      let newPlatforms;
      if (editingPlatform) {
          newPlatforms = platforms.map(p => p.id === platform.id ? platform : p);
      } else {
          newPlatforms = [...platforms, platform];
      }
      setPlatforms(newPlatforms);
      await saveAIPlatforms(newPlatforms);
      setIsModalOpen(false);
  };

  const handleDelete = async (id: string) => {
      if (sourceTagId) return;
      const newPlatforms = platforms.filter(p => p.id !== id);
      setPlatforms(newPlatforms);
      await saveAIPlatforms(newPlatforms);
      setIsModalOpen(false);
  };

  if (platforms.length === 0) {
      if (sourceTagId) return null;
      // Empty state for manual management
       return (
        <div className="w-full max-w-6xl mx-auto px-4 text-center">
            <button 
               onClick={() => { setEditingPlatform(null); setIsModalOpen(true); }}
               className={`inline-flex items-center justify-center gap-2 p-3 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-white border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl hover:border-blue-500 transition-all ${variant === 'card' ? 'w-32 h-32 flex-col' : 'px-4 py-2'}`}
           >
               <MoreHorizontal size={20} />
               <span className="text-sm font-medium">添加 AI</span>
           </button>
           <EditModal 
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            platform={editingPlatform}
            onSave={handleSave}
            onDelete={handleDelete}
           />
        </div>
       );
  }

  return (
    <div className="w-full max-w-[98%] mx-auto px-0">
       <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={platforms.map(p => p.id)} strategy={horizontalListSortingStrategy}>
            <div className={`flex flex-wrap gap-4 ${variant === 'compact' ? 'justify-center' : ''}`}>
                {platforms.map((platform) => (
                    <SortableAIItem key={platform.id} platform={platform} onEdit={handleEdit} isReadOnly={!!sourceTagId} variant={variant} />
                ))}
                 {!sourceTagId && (
                     <button 
                        onClick={() => { setEditingPlatform(null); setIsModalOpen(true); }}
                        className={variant === 'card' 
                            ? "flex flex-col items-center justify-center gap-3 p-4 bg-white/50 dark:bg-white/5 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 hover:border-blue-500 hover:text-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-all w-32 h-32 text-slate-400 dark:text-slate-500"
                            : "flex flex-col items-center justify-center gap-2 p-2 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 hover:border-blue-500 hover:text-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-all w-20 h-auto text-slate-400 dark:text-slate-500 group"
                        }
                    >
                        <div className={`rounded-xl flex items-center justify-center ${variant === 'card' ? '' : 'w-12 h-12 border border-transparent'}`}>
                             <MoreHorizontal size={variant === 'card' ? 24 : 20} />
                        </div>
                        <span className={variant === 'card' ? "text-sm font-medium" : "text-xs font-medium"}>添加</span>
                    </button>
                 )}
            </div>
        </SortableContext>
       </DndContext>

       {!sourceTagId && (
           <EditModal 
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            platform={editingPlatform}
            onSave={handleSave}
            onDelete={handleDelete}
           />
       )}
    </div>
  );
};

export default AIPlatforms;
